// ─── Fiche INFO d'un contact (lecture seule) ──────────────────────
// Valeurs cliquables : 📞 appeler (tel:), ✉️ email (mailto:), 📍 itinéraire.
// Multi-valeurs typées (le principal ⭐ en tête) + carte du lieu principal.
// ✏️ en en-tête → passe à l'édition (via onEditer).
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Linking } from 'react-native';
import { Feuille, CarteApercu, lienItineraire, demanderItineraire, frJMA, useUI, useT } from '@mop/core';
import {
  emojiRole, labelRole, nomAffiche, TYPES_TEL, TYPES_EMAIL, emojiType, labelType,
  normaliserTels, normaliserEmails, normaliserLieux, normaliserChamps, urlChamp, libelleHoraire,
} from './services/contacts';

const principalDabord = (arr) => [...arr].sort((a, b) => (b.principal ? 1 : 0) - (a.principal ? 1 : 0));

export default function FicheContactInfo({ visible, contact, lieux = [], onFermer, onEditer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const c = contact || {};
  const ouvrir = (url) => { if (url) Linking.openURL(url).catch(() => {}); };

  const tels = principalDabord(normaliserTels(c));
  const emails = principalDabord(normaliserEmails(c));
  const lieuxC = principalDabord(normaliserLieux(c)).map(e => ({ ...e, lieu: lieux.find(l => l.id === e.lieuId) })).filter(e => e.lieu);
  const lieuPrincipal = lieuxC[0]?.lieu;
  const coordsPrincipal = lieuPrincipal && lieuPrincipal.lat != null && lieuPrincipal.lng != null
    ? { lat: lieuPrincipal.lat, lng: lieuPrincipal.lng } : null;

  // `lignes` : un horaire tient sur plusieurs lignes (une par plage) — le
  // plafond de 2 lignes des autres valeurs le tronquerait.
  // `onPress` : une adresse ne s'ouvre pas comme un tel:/mailto: — elle passe
  // par le choix de l'app de navigation.
  const Ligne = ({ ic, val, sous, principal, url, onPress, lignes = 2 }) => (
    <TouchableOpacity style={s.row} activeOpacity={(url || onPress) ? 0.6 : 1}
      onPress={() => (onPress ? onPress() : ouvrir(url))} disabled={!url && !onPress}>
      <Text style={{ fontSize: 17 }}>{ic}</Text>
      <View style={{ flex: 1, minWidth: 0 }}>
        {!!sous && <Text style={s.sous}>{sous}{principal ? '  ⭐' : ''}</Text>}
        <Text style={[s.val, url && { color: UI.accent, fontWeight: '700' }]} numberOfLines={lignes}>{val}</Text>
      </View>
    </TouchableOpacity>
  );

  return (
    <Feuille visible={visible} titre={nomAffiche(c)} emoji={emojiRole(c.role)} hauteur={0.8} onFermer={onFermer}
      action={{ icone: '✏️', label: t('commun.editer'), onPress: onEditer }}>
      {!!c.surnom && <Text style={s.surnom}>« {c.surnom} »</Text>}
      <Text style={s.cat}>{emojiRole(c.role)}  {labelRole(c.role)}</Text>

      {tels.map((e, i) => (
        <Ligne key={`t${i}`} ic={emojiType(TYPES_TEL, e.type) || '📞'} sous={labelType(TYPES_TEL, e.type)}
          principal={e.principal} val={e.valeur} url={`tel:${String(e.valeur).replace(/\s/g, '')}`} />
      ))}
      {emails.map((e, i) => (
        <Ligne key={`m${i}`} ic={emojiType(TYPES_EMAIL, e.type) || '✉️'} sous={labelType(TYPES_EMAIL, e.type)}
          principal={e.principal} val={e.valeur} url={`mailto:${e.valeur}`} />
      ))}
      {/* Carte SOUS l'adresse de chaque lieu. `adresse` est passée même sans
          coordonnées : CarteApercu géocode alors — la plupart des lieux du
          carnet n'ont pas de lat/lng enregistrés. */}
      {lieuxC.map((e, i) => {
        const co = e.lieu.lat != null && e.lieu.lng != null ? { lat: e.lieu.lat, lng: e.lieu.lng } : null;
        const adr = e.lieu.adresse || e.lieu.nom;
        return (
          <View key={`l${i}`}>
            <Ligne ic="📍" sous={e.lieu.nom} principal={e.principal} val={adr}
              onPress={lienItineraire({ ...(co || {}), adresse: adr })
                ? () => demanderItineraire({ ...(co || {}), adresse: adr }, { titre: t('lieux.itineraire'), annuler: t('commun.annuler') })
                : undefined} />
            {!!e.lieu.adresse && <CarteApercu coords={co} adresse={e.lieu.adresse} />}
          </View>
        );
      })}

      {normaliserChamps(c).filter(ch => (ch.valeur || '').trim()).map((ch, i) => (
        <Ligne key={`c${i}`} ic={ch.emoji || '🏷️'} sous={ch.nom}
          lignes={ch.saisie === 'horaire' ? 0 : 2}
          val={ch.saisie === 'horaire' ? libelleHoraire(ch.valeur)
            : (ch.saisie === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(ch.valeur) ? frJMA(ch.valeur) : ch.valeur)}
          url={urlChamp(ch)} />
      ))}

      {!!c.note && (<><Text style={s.lbl}>{t('contacts.note')}</Text><Text style={s.note}>{c.note}</Text></>)}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  surnom: { fontSize: 14, color: UI.muted, fontStyle: 'italic', marginBottom: 4 },
  cat: { fontSize: 13, color: UI.muted, fontWeight: '700', marginBottom: 6 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 10, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  sous: { fontSize: 11.5, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 1 },
  val: { fontSize: 14.5, color: UI.ink },
  lbl: { fontSize: 12, color: UI.faint, fontWeight: '700', marginTop: 14, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 },
  note: { fontSize: 14, color: UI.ink, lineHeight: 20 },
});
